import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import './PeoplePicker.css';

/**
 * Choose any number of people, with a search box - and "nobody".
 *
 * Built for the All tickets filters (requester, assigned to, contractor) and
 * the Invoicing contractor filter, which all have the same shape: a list that
 * can run to dozens of names, where the useful answers are "these three" or
 * "nobody at all". A <select multiple> does neither well - it needs Ctrl to
 * choose a second name, has no search, and cannot say "Unassigned" without
 * pretending it is a person.
 *
 * `value` is an array of ids, with the string 'none' standing for the
 * nobody option when `noneLabel` is given. The component holds no selection
 * of its own: every change goes straight to `onChange`, so the URL (which is
 * where the pages keep their filters) stays the one source of truth.
 *
 * KEYBOARD. The trigger is a button: Enter, Space or ArrowDown opens it and
 * puts the cursor in the search box. ArrowDown/ArrowUp move through the
 * options, Space or Enter toggles the one with focus, Home/End jump, and
 * Escape closes and gives focus back to the trigger. Typing in the search box
 * narrows the list as you go.
 *
 * `inline` renders the list directly instead of behind a trigger - used
 * inside the phone filter sheet, where a popover inside a sheet is a popover
 * too many.
 */
export default function PeoplePicker({
  label, options = [], value = [], onChange,
  noneLabel = null, noneCount = null, allLabel = 'Anyone',
  inline = false, idBase
}) {
  const auto = useId();
  const base = idBase || `pp-${auto.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Which edge the panel hangs from: the trigger's left, unless that would
  // carry it past the right of the window - the last picker in a row sits
  // near the edge, and a panel off-screen makes the whole page scroll sideways.
  const [alignRight, setAlignRight] = useState(false);
  const wrap = useRef(null);
  const trigger = useRef(null);
  const search = useRef(null);
  const listRef = useRef(null);

  const chosen = useMemo(() => new Set(value || []), [value]);
  const byId = useMemo(() => new Map(options.map((o) => [String(o.id), o])), [options]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = [
      ...(noneLabel ? [{ id: 'none', name: noneLabel, n: noneCount, none: true }] : []),
      ...options
    ];
    if (!q) return all;
    return all.filter((o) => `${o.name} ${o.company || ''}`.toLowerCase().includes(q));
  }, [options, noneLabel, noneCount, query]);

  // Close on a press outside; Escape is handled on the panel itself.
  useEffect(() => {
    if (!open || inline) return undefined;
    const onPointer = (e) => {
      if (wrap.current && !wrap.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
    };
  }, [open, inline]);

  useLayoutEffect(() => {
    if (!open || inline || !trigger.current) return;
    const r = trigger.current.getBoundingClientRect();
    const width = Math.min(272, window.innerWidth - 32);
    setAlignRight(r.left + width > window.innerWidth - 8);
  }, [open, inline]);

  useEffect(() => {
    if (open && search.current) search.current.focus({ preventScroll: true });
    if (!open) setQuery('');
  }, [open]);

  const toggle = (id) => {
    const key = String(id);
    const next = new Set(chosen);
    if (next.has(key)) next.delete(key); else next.add(key);
    onChange([...next]);
  };

  const summary = (() => {
    const ids = [...chosen];
    if (ids.length === 0) return allLabel;
    const name = (id) => (id === 'none' ? noneLabel : (byId.get(id)?.name || 'Someone'));
    if (ids.length === 1) return name(ids[0]);
    return `${name(ids[0])} +${ids.length - 1}`;
  })();

  const items = () => (listRef.current
    ? [...listRef.current.querySelectorAll('input[type="checkbox"]')] : []);

  const onListKey = (e) => {
    const all = items();
    const at = all.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      (all[Math.min(all.length - 1, at + 1)] || all[0])?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (at <= 0) search.current?.focus(); else all[at - 1].focus();
    } else if (e.key === 'Home') {
      e.preventDefault(); all[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault(); all[all.length - 1]?.focus();
    } else if (e.key === 'Enter' && at >= 0) {
      // Space toggles a checkbox natively; Enter is taught the same.
      e.preventDefault();
      all[at].click();
    }
  };

  const onPanelKey = (e) => {
    if (e.key === 'Escape' && !inline) {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      trigger.current?.focus();
    }
  };

  const onSearchKey = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      items()[0]?.focus();
    } else if (e.key === 'Enter') {
      // Enter in the search box chooses the only match - the quickest way to
      // say "Marco" is to type "mar" and press Enter.
      e.preventDefault();
      if (shown.length === 1) toggle(shown[0].id);
    }
  };

  const panel = (
    <div className={inline ? 'pp-panel is-inline' : `pp-panel${alignRight ? ' is-right' : ''}`}
         id={`${base}-panel`} role="group" aria-label={`${label} filter`}
         onKeyDown={onPanelKey}>
      <div className="pp-search">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8"
             strokeLinecap="round" aria-hidden="true">
          <circle cx="7" cy="7" r="4.5" /><path d="m10.5 10.5 3 3" />
        </svg>
        <input
          ref={search}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onSearchKey}
          placeholder={`Find ${label.toLowerCase()}…`}
          aria-label={`Find ${label.toLowerCase()}`}
          aria-controls={`${base}-list`}
        />
      </div>
      <ul className="pp-list" id={`${base}-list`} ref={listRef} onKeyDown={onListKey}>
        {shown.length === 0 && <li className="pp-empty">Nobody matches “{query}”</li>}
        {shown.map((o) => {
          const id = String(o.id);
          const on = chosen.has(id);
          return (
            <li key={id} className={o.none ? 'pp-item is-none' : 'pp-item'}>
              <label className={on ? 'pp-option is-on' : 'pp-option'}>
                <input type="checkbox" checked={on} onChange={() => toggle(id)}
                       data-value={id} />
                <span className="pp-name">
                  {o.name}
                  {o.company && <small>{o.company}</small>}
                  {o.kind === 'contractor' && <small>Contractor</small>}
                </span>
                {Number.isFinite(o.n) && <span className="pp-n">{o.n}</span>}
              </label>
            </li>
          );
        })}
      </ul>
      {chosen.size > 0 && (
        <div className="pp-foot">
          <span>{chosen.size} chosen</span>
          <button type="button" className="pp-clear" onClick={() => onChange([])}>Clear</button>
        </div>
      )}
    </div>
  );

  if (inline) {
    return (
      <fieldset className="pp pp-inline" data-filter={idBase}>
        <legend>{label}</legend>
        {panel}
      </fieldset>
    );
  }

  return (
    <div className="pp" ref={wrap} data-filter={idBase}>
      <button
        ref={trigger}
        type="button"
        className={chosen.size ? 'pp-trigger is-set' : 'pp-trigger'}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? `${base}-panel` : undefined}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !open) { e.preventDefault(); setOpen(true); }
        }}
      >
        <span className="pp-label">{label}</span>
        <span className="pp-value">{summary}</span>
        <svg className="pp-chev" viewBox="0 0 16 16" fill="none" stroke="currentColor"
             strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 6.5 8 10.5 12 6.5" />
        </svg>
      </button>
      {open && panel}
    </div>
  );
}
